import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Beer, Plus, Minus, ShoppingCart, Copy, Clock, History } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, API, barImageUrl, brl, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import FloatingMenuButton from "@/components/FloatingMenuButton";
import BannerCarousel from "@/components/BannerCarousel";
import usePageTexts from "@/hooks/usePageTexts";

const LOGO_URL =
  "https://customer-assets-rejwkqb3.emergentagent.net/job_arena-dubal/artifacts/dgj3odz1_Logo%20Dubal%20branca.png";

export default function BarPage() {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState({});
  const [settings, setSettings] = useState({ bar_pix_key: "", bar_pix_holder: "", bar_logo: false });
  const [logo, setLogo] = useState(LOGO_URL);
  const [checkout, setCheckout] = useState(null); // order result
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [placing, setPlacing] = useState(false);

  const pending = user && !user.approved && user.role !== "admin";
  const t = usePageTexts();

  const load = () => {
    api.get("/bar/products").then((res) => setProducts(res.data)).catch(() => {});
    api.get("/settings").then((res) => {
      setSettings(res.data);
      if (res.data.bar_logo) setLogo(`${API}/settings/bar-logo?v=${encodeURIComponent(res.data.bar_logo_updated_at)}`);
    }).catch(() => {});
    api.get("/me/orders").then((res) => setHistory(res.data)).catch(() => {});
  };
  useEffect(load, []);

  const add = (id) => setCart((c) => ({ ...c, [id]: (c[id] || 0) + 1 }));
  const sub = (id) => setCart((c) => { const n = (c[id] || 0) - 1; const nc = { ...c }; if (n <= 0) delete nc[id]; else nc[id] = n; return nc; });

  const items = useMemo(() => products.filter((p) => cart[p.id]), [products, cart]);
  const total = useMemo(() => items.reduce((s, p) => s + p.price * cart[p.id], 0), [items, cart]);
  const count = useMemo(() => Object.values(cart).reduce((a, b) => a + b, 0), [cart]);

  const categories = useMemo(() => {
    const map = {};
    products.forEach((p) => { (map[p.category || "Outros"] = map[p.category || "Outros"] || []).push(p); });
    return map;
  }, [products]);

  const finish = async () => {
    if (count === 0) return;
    setPlacing(true);
    try {
      const payload = { items: items.map((p) => ({ product_id: p.id, qty: cart[p.id] })), note: "" };
      const res = await api.post("/bar/orders", payload);
      setCheckout(res.data);
      setCart({});
      api.get("/me/orders").then((r) => setHistory(r.data)).catch(() => {});
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setPlacing(false);
    }
  };

  const copyPix = () => { navigator.clipboard?.writeText(settings.bar_pix_key); toast.success("Chave PIX copiada!"); };

  return (
    <div className="min-h-screen bg-[#FBFBFB] pb-28" data-testid="bar-page">
      <header className="relative overflow-hidden bg-[#0b1610]">
        {settings.menu_bg?.bar_header?.present && (
          <img data-testid="bar-header-bg" src={`${API}/settings/menu-bg/bar_header?v=${encodeURIComponent(settings.menu_bg.bar_header.updated_at || "")}`} alt="" className="absolute inset-0 h-full w-full object-cover" />
        )}
        <div className={`absolute inset-0 bg-gradient-to-br from-[#0b1610] via-[#123021] to-[#0b1610] ${settings.menu_bg?.bar_header?.present ? "opacity-60" : ""}`} />
        <div className="relative mx-auto max-w-4xl px-5 py-10 text-center">
          <Link to="/" data-testid="bar-back" className="absolute left-5 top-5 inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 text-xs font-semibold text-white/90 backdrop-blur transition hover:bg-white/20">
            <ArrowLeft className="h-3.5 w-3.5" /> Menu
          </Link>
          <button data-testid="bar-history-btn" onClick={() => setShowHistory(true)} className="absolute right-5 top-5 inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 text-xs font-semibold text-white/90 backdrop-blur transition hover:bg-white/20">
            <History className="h-3.5 w-3.5" /> Meus pedidos
          </button>
          <img src={logo} alt="Dubal" className="mx-auto w-40" />
          <h1 data-testid="bar-title" className="mt-3 font-display text-3xl font-black text-white sm:text-4xl">{t("bar_title")}</h1>
          <p className="mt-1 text-sm text-emerald-100/70">{t("bar_subtitle")}</p>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-5 py-8">
        {pending && (
          <div data-testid="bar-pending" className="mb-6 flex items-center gap-3 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-amber-800">
            <Clock className="h-5 w-5 shrink-0" />
            <p className="text-sm">Sua conta está aguardando aprovação. Você poderá finalizar pedidos assim que for aprovada.</p>
          </div>
        )}

        <BannerCarousel placement="bar" className="mb-8" />

        {Object.entries(categories).map(([cat, list]) => (
          <section key={cat} className="mb-8">
            <h2 className="mb-3 font-display text-xl font-extrabold text-slate-900">{cat}</h2>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {list.map((p) => (
                <div key={p.id} data-testid={`bar-product-${p.id}`} className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-3">
                  <div className="h-16 w-16 shrink-0 overflow-hidden rounded-xl bg-slate-100">
                    {barImageUrl(p) ? <img src={barImageUrl(p)} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full w-full items-center justify-center"><Beer className="h-6 w-6 text-slate-300" /></div>}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-display text-base font-bold text-slate-900">{p.name}</p>
                    {p.description && <p className="truncate text-xs text-slate-500">{p.description}</p>}
                    <p className="mt-0.5 font-bold text-[#1B7A45]">{brl(p.price)}</p>
                  </div>
                  {cart[p.id] ? (
                    <div className="flex items-center gap-2">
                      <Button data-testid={`bar-sub-${p.id}`} onClick={() => sub(p.id)} size="icon" variant="outline" className="h-8 w-8 rounded-full border-slate-200"><Minus className="h-4 w-4" /></Button>
                      <span className="w-5 text-center font-bold">{cart[p.id]}</span>
                      <Button data-testid={`bar-add-${p.id}`} onClick={() => add(p.id)} size="icon" className="h-8 w-8 rounded-full bg-[#1B7A45] hover:bg-[#156337]"><Plus className="h-4 w-4" /></Button>
                    </div>
                  ) : (
                    <Button data-testid={`bar-add-${p.id}`} onClick={() => add(p.id)} size="sm" className="rounded-full bg-[#1B7A45] hover:bg-[#156337]"><Plus className="mr-1 h-4 w-4" /> Add</Button>
                  )}
                </div>
              ))}
            </div>
          </section>
        ))}
      </main>

      {/* Sticky cart */}
      {count > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/95 backdrop-blur">
          <div className="mx-auto flex max-w-4xl items-center justify-between px-5 py-3">
            <div className="flex items-center gap-2 text-slate-800">
              <ShoppingCart className="h-5 w-5 text-[#1B7A45]" />
              <span className="font-semibold">{count} item(ns)</span>
              <span className="font-display text-lg font-black text-[#1B7A45]">{brl(total)}</span>
            </div>
            <Button data-testid="bar-checkout-btn" onClick={finish} disabled={placing || pending} className="rounded-full bg-[#E8611A] px-6 py-5 font-semibold text-white hover:bg-[#CF5212] disabled:opacity-50">
              {placing ? "Enviando..." : "Finalizar compra"}
            </Button>
          </div>
        </div>
      )}

      {/* Checkout PIX dialog */}
      <Dialog open={!!checkout} onOpenChange={(o) => !o && setCheckout(null)}>
        <DialogContent data-testid="bar-pix-dialog" className="max-w-md rounded-3xl">
          <DialogHeader><DialogTitle className="font-display text-2xl">Pedido enviado! 🎉</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Pague com PIX e aguarde a confirmação do administrador.</p>
          <div className="mt-2 rounded-2xl bg-[#122018] p-4 text-white">
            <p className="text-xs font-semibold uppercase tracking-wider text-emerald-300">PIX {settings.bar_pix_holder ? `· ${settings.bar_pix_holder}` : ""}</p>
            <div className="mt-1 flex items-center justify-between gap-2">
              <span className="font-display text-lg font-black">{settings.bar_pix_key}</span>
              <button data-testid="bar-pix-copy" onClick={copyPix} className="rounded-lg bg-white/10 p-2 hover:bg-white/20"><Copy className="h-4 w-4" /></button>
            </div>
          </div>
          <div className="mt-3 flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3">
            <span className="text-sm text-slate-500">Total do pedido</span>
            <span className="font-display text-xl font-black text-[#E8611A]">{brl(checkout?.total || 0)}</span>
          </div>
          <Button onClick={() => setCheckout(null)} className="mt-2 w-full rounded-full bg-[#1B7A45] py-5 font-semibold hover:bg-[#156337]">Entendi</Button>
        </DialogContent>
      </Dialog>

      {/* History dialog */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent data-testid="bar-history-dialog" className="max-w-md rounded-3xl">
          <DialogHeader><DialogTitle className="font-display text-2xl">Meus pedidos</DialogTitle></DialogHeader>
          <div className="max-h-96 space-y-2 overflow-y-auto">
            {history.length === 0 ? (
              <p className="py-8 text-center text-sm text-slate-400">Nenhum pedido ainda.</p>
            ) : history.map((o) => (
              <div key={o.id} className="rounded-xl border border-slate-200 p-3">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-[#1B7A45]">{brl(o.total)}</span>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${o.status === "confirmada" ? "bg-emerald-100 text-emerald-700" : o.status === "cancelada" ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-700"}`}>{o.status}</span>
                </div>
                <p className="mt-1 text-xs text-slate-500">{o.items.map((i) => `${i.qty}x ${i.name}`).join(", ")}</p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {count === 0 && <FloatingMenuButton />}
    </div>
  );
}
